
import React from 'react';

interface Testimonial {
  id: number;
  name: string;
  property: string;
  location: string;
  image: string;
  rating: number;
  quote: string;
  results: string;
}

const TestimonialsSection = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      location: "San Francisco, CA",
      property: "Downtown Loft",
      rating: 5,
      text: "Vista Boost transformed my listing completely! My booking rate went from 45% to 78% in just 6 weeks. The photos look so professional that guests often comment they look even better than the actual space. My revenue increased by $1,200 per month!",
      results: {
        bookingIncrease: "73%",
        revenueIncrease: "$1,200/month",
        timeframe: "6 weeks"
      },
      beforeAfter: {
        before: "45% booking rate",
        after: "78% booking rate"
      },
      image: "https://public.readdy.ai/ai/img_res/0e1f9839840f1fb00c2c9afb96fa5a47.jpg"
    },
    {
      id: 2,
      name: "Marcus Rodriguez",
      location: "Austin, TX",
      property: "Cozy Bungalow",
      rating: 5,
      text: "I was skeptical about spending money on photo editing, but the ROI was incredible. My nightly rate increased by $35, and I'm now booked solid 3 months in advance. The photos capture the warmth and character of my bungalow perfectly. Best investment I've made for my Airbnb!",
      results: {
        bookingIncrease: "85%",
        revenueIncrease: "$1,050/month",
        timeframe: "4 weeks"
      },
      beforeAfter: {
        before: "$95/night average",
        after: "$130/night average"
      },
      image: "https://readdy.ai/api/search-image?query=Friendly%20Hispanic%20man%20in%20his%2030s%20standing%20in%20charming%20renovated%20bungalow%20with%20vintage%20decor%2C%20successful%20property%20owner%2C%20natural%20lighting&width=80&height=80&seq=testimonial2&orientation=squarish"
    },
    {
      id: 3,
      name: "Jennifer Walsh",
      location: "Portland, OR",
      property: "Modern Studio",
      rating: 5,
      text: "The before and after difference is night and day! My studio went from looking cramped and dark to spacious and bright. I've had 3 guests specifically mention how the photos accurately represented the space but somehow made it look even more appealing. Bookings doubled in 2 months!",
      results: {
        bookingIncrease: "120%",
        revenueIncrease: "$950/month",
        timeframe: "8 weeks"
      },
      beforeAfter: {
        before: "2-3 bookings/month",
        after: "7-8 bookings/month"
      },
      image: "https://readdy.ai/api/search-image?query=Professional%20woman%20with%20red%20hair%20smiling%20in%20bright%20modern%20studio%20apartment%20with%20minimalist%20design%2C%20successful%20Airbnb%20host%2C%20natural%20window%20lighting&width=80&height=80&seq=testimonial3&orientation=squarish"
    },
    {
      id: 4,
      name: "David Kim",
      location: "Miami, FL",
      property: "Beachside Condo",
      rating: 5,
      text: "Vista Boost's editing made my condo photos pop! The HDR processing balanced the bright ocean views with the interior perfectly. My listing moved from page 3 to page 1 in search results. I'm now charging premium rates and staying booked year-round. The 24-hour turnaround was amazing!",
      results: {
        bookingIncrease: "95%",
        revenueIncrease: "$1,400/month",
        timeframe: "5 weeks"
      },
      beforeAfter: {
        before: "Page 3 search results",
        after: "Page 1 search results"
      },
      image: "https://readdy.ai/api/search-image?query=Asian%20man%20in%20casual%20shirt%20standing%20on%20balcony%20of%20modern%20beachside%20condo%20with%20ocean%20view%2C%20successful%20property%20investor%2C%20golden%20hour%20lighting&width=80&height=80&seq=testimonial4&orientation=squarish"
    },
    {
      id: 5,
      name: "Amanda Foster",
      location: "Nashville, TN",
      property: "Music City Retreat",
      rating: 5,
      text: "As a new host, I was struggling to get bookings. Vista Boost's professional editing made my photos look like they belonged in a luxury hotel magazine. My occupancy rate went from 30% to 82%, and I'm now a Superhost! The investment paid for itself in 3 weeks.",
      results: {
        bookingIncrease: "173%",
        revenueIncrease: "$1,100/month",
        timeframe: "3 weeks"
      },
      beforeAfter: {
        before: "30% occupancy rate",
        after: "82% occupancy rate"
      },
      image: "https://readdy.ai/api/search-image?query=Young%20blonde%20woman%20smiling%20proudly%20in%20stylish%20Nashville%20apartment%20with%20music%20themed%20decor%2C%20new%20successful%20Airbnb%20Superhost%2C%20warm%20interior%20lighting&width=80&height=80&seq=testimonial5&orientation=squarish"
    },
    {
      id: 6,
      name: "Robert Thompson",
      location: "Denver, CO",
      property: "Mountain View Cabin",
      rating: 5,
      text: "The team captured the rustic charm of my cabin while making it look upscale and inviting. My average guest rating increased from 4.2 to 4.8 stars, and guests constantly praise the accuracy of the photos. Revenue is up 60% and I'm booked through next ski season!",
      results: {
        bookingIncrease: "60%",
        revenueIncrease: "$800/month",
        timeframe: "6 weeks"
      },
      beforeAfter: {
        before: "4.2 star rating",
        after: "4.8 star rating"
      },
      image: "https://readdy.ai/api/search-image?query=Middle-aged%20man%20with%20beard%20in%20flannel%20shirt%20standing%20in%20rustic%20mountain%20cabin%20with%20wood%20interior%20and%20mountain%20views%2C%20outdoor%20enthusiast%20host&width=80&height=80&seq=testimonial6&orientation=squarish"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Real Results from Real Hosts</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how professional photo editing transformed these Airbnb listings and dramatically increased bookings and revenue
          </p>
          
          {/* Results Summary */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Average Results Across All Clients</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  <a href="/roi-calculator" className="hover:text-blue-700 underline">
                    89%
                  </a>
                </div>
                <div className="text-gray-600">Average Booking Increase</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  <a href="/roi-calculator" className="hover:text-green-700 underline">
                    $1,100
                  </a>
                </div>
                <div className="text-gray-600">Average Monthly Revenue Boost</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  <a href="/pricing-guide" className="hover:text-purple-700 underline">
                    5.2 weeks
                  </a>
                </div>
                <div className="text-gray-600">Average ROI Timeline</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">4.7★</div>
                <div className="text-gray-600">Average Guest Rating</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start mb-6">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full object-cover object-top mr-4"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{testimonial.name}</h3>
                    <div className="flex text-yellow-400">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{testimonial.location} • {testimonial.property}</p>
                </div>
              </div>

              {/* Results Metrics */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{testimonial.results.bookingIncrease}</div>
                    <div className="text-sm text-gray-600">Booking Increase</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{testimonial.results.revenueIncrease}</div>
                    <div className="text-sm text-gray-600">Revenue Boost</div>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <div className="text-sm text-gray-600">
                    <strong>Results achieved in:</strong> {testimonial.results.timeframe}
                  </div>
                </div>
              </div>

              {/* Before/After Comparison */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-center">Before vs After</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-red-600 font-medium">Before</div>
                    <div className="text-sm text-gray-600">{testimonial.beforeAfter.before}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-green-600 font-medium">After</div>
                    <div className="text-sm text-gray-600">{testimonial.beforeAfter.after}</div>
                  </div>
                </div>
              </div>

              <blockquote className="text-gray-700 leading-relaxed italic">
                "{testimonial.text}"
              </blockquote>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Ready to Join These Success Stories?</h3>
            <p className="text-blue-100 mb-6 text-lg">
              Get the same professional photo editing that helped these hosts increase their bookings by an average of{' '}
              <a href="/roi-calculator" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">
                89%
              </a>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Start Your Transformation
              </button>
              <button 
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Our Portfolio
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
